import { useEffect, useState, useCallback, useRef } from 'react';
import {
  InfoCard,
  Page,
  Header,
  Content,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import StorageIcon from '@material-ui/icons/Storage';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

// ── Types ──────────────────────────────────────────────────────────────────

type DiskStat = {
  pod: string;
  used: string;
  available: string;
  usePercent: number;
  capacity: string;
  status: 'ok' | 'warning' | 'critical' | 'error';
  error?: string;
};

type ImageStats = {
  pod: string;
  total: { count: number; size: string };
  dangling: { count: number; size: string };
  active: { count: number; size: string };
  unused: { count: number; size: string };
  buildCache: { count: number; size: string };
  volumes: { count: number; size: string };
  error?: string;
};

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  chipOk: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  chipWarning: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  chipCritical: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  chipError: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  percentCell: {
    fontWeight: 600,
  },
  autoRefreshNote: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

// ── Status Chip ────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: DiskStat['status'] }) {
  const classes = useStyles();
  const chipMap: Record<string, { label: string; className: string }> = {
    ok: { label: 'OK', className: classes.chipOk },
    warning: { label: 'Warning', className: classes.chipWarning },
    critical: { label: 'Critical', className: classes.chipCritical },
    error: { label: 'Error', className: classes.chipError },
  };
  const { label, className } = chipMap[status] ?? chipMap.error;
  return <Chip size="small" label={label} className={className} />;
}

// ── Main Component ─────────────────────────────────────────────────────────

export const RunnerOpsContent = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  const [stats, setStats] = useState<DiskStat[] | null>(null);
  const [imageStats, setImageStats] = useState<ImageStats[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pruning, setPruning] = useState<string | null>(null); // pod name or 'all'
  const [purging, setPurging] = useState<string | null>(null); // pod name or 'all'
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const backendUrl = config.getString('backend.baseUrl');

  const fetchDiskUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [diskResp, imgResp] = await Promise.all([
        backstageFetch(`${backendUrl}/api/runner-ops/disk-usage`),
        backstageFetch(`${backendUrl}/api/runner-ops/image-stats`),
      ]);
      if (diskResp.status === 403 || diskResp.status === 401) {
        const body = await diskResp.json().catch(() => ({}));
        setError(body.error || 'Access denied — platform-team membership required.');
        setStats(null);
        setImageStats(null);
        return;
      }
      if (!diskResp.ok) {
        throw new Error(`Server returned ${diskResp.status}`);
      }
      const diskData: DiskStat[] = await diskResp.json();
      setStats(diskData);

      if (imgResp.ok) {
        const imgData: ImageStats[] = await imgResp.json();
        setImageStats(imgData);
      }

      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch disk usage');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, backstageFetch]);

  const handlePrune = useCallback(
    async (pod?: string) => {
      const target = pod ?? 'all';
      setPruning(target);
      try {
        const resp = await backstageFetch(`${backendUrl}/api/runner-ops/prune`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pod ? { pod } : {}),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error || `Prune failed: ${resp.status}`);
        }
        // Refresh stats after prune
        await fetchDiskUsage();
      } catch (err: any) {
        setError(err.message || 'Prune failed');
      } finally {
        setPruning(null);
      }
    },
    [backendUrl, backstageFetch, fetchDiskUsage],
  );

  const handlePurge = useCallback(
    async (pod?: string) => {
      const target = pod ?? 'all';
      setPurging(target);
      try {
        const resp = await backstageFetch(`${backendUrl}/api/runner-ops/purge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pod ? { pod } : {}),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error || `Purge failed: ${resp.status}`);
        }
        await fetchDiskUsage();
      } catch (err: any) {
        setError(err.message || 'Purge failed');
      } finally {
        setPurging(null);
      }
    },
    [backendUrl, backstageFetch, fetchDiskUsage],
  );

  // Initial load + 30s auto-refresh
  useEffect(() => {
    fetchDiskUsage();
    timerRef.current = setInterval(fetchDiskUsage, 30_000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchDiskUsage]);

  return (
    <Page themeId="tool">
      <Header title="Runner Disk Health" subtitle="Gitea Actions DinD storage per replica" />
      <Content>
        <InfoCard>
      <div className={classes.toolbar}>
        <Box>
          <Typography variant="body2" className={classes.autoRefreshNote}>
            Auto-refreshes every 30s
            {lastRefresh && ` · Last: ${lastRefresh.toLocaleTimeString()}`}
          </Typography>
        </Box>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchDiskUsage()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            color="secondary"
            startIcon={pruning === 'all' ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
            onClick={() => handlePrune()}
            disabled={!!pruning || !!purging}
          >
            Prune All
          </Button>
          <Tooltip title="Removes ALL unused images, build cache, and volumes — CI builds will need to re-pull">
            <Button
              variant="contained"
              size="small"
              style={{ backgroundColor: '#d32f2f', color: '#fff' }}
              startIcon={purging === 'all' ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
              onClick={() => {
                if (window.confirm('This will remove ALL unused Docker images, build cache, and volumes on every runner. CI builds will need to re-pull images. Continue?')) {
                  handlePurge();
                }
              }}
              disabled={!!pruning || !!purging}
            >
              Purge All
            </Button>
          </Tooltip>
        </div>
      </div>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {loading && !stats && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {stats && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pod</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Available</TableCell>
              <TableCell>% Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.map(row => (
              <TableRow key={row.pod}>
                <TableCell>{row.pod}</TableCell>
                <TableCell>{row.capacity}</TableCell>
                <TableCell>{row.used}</TableCell>
                <TableCell>{row.available}</TableCell>
                <TableCell className={classes.percentCell}>
                  {row.status === 'error' ? '—' : `${row.usePercent}%`}
                </TableCell>
                <TableCell>
                  <StatusChip status={row.status} />
                  {row.error && (
                    <Tooltip title={row.error}>
                      <Typography variant="caption" color="error" style={{ marginLeft: 8 }}>
                        {row.error.length > 40 ? `${row.error.slice(0, 40)}…` : row.error}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" style={{ gap: 4 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        pruning === row.pod ? (
                          <CircularProgress size={14} />
                        ) : (
                          <DeleteSweepIcon />
                        )
                      }
                      onClick={() => handlePrune(row.pod)}
                      disabled={!!pruning || !!purging}
                    >
                      Prune
                    </Button>
                    <Tooltip title="Remove ALL unused images, cache, and volumes on this runner">
                      <Button
                        variant="outlined"
                        size="small"
                        style={{ borderColor: '#d32f2f', color: '#d32f2f' }}
                        startIcon={
                          purging === row.pod ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteForeverIcon />
                          )
                        }
                        onClick={() => {
                          if (window.confirm(`Purge ALL unused Docker data on ${row.pod}? This removes cached images, build cache, and volumes.`)) {
                            handlePurge(row.pod);
                          }
                        }}
                        disabled={!!pruning || !!purging}
                      >
                        Purge
                      </Button>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InfoCard>

    {imageStats && (
      <Box mt={3}>
        <InfoCard title="Docker Image Breakdown" subheader="Per-runner image classification">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pod</TableCell>
                <TableCell>Total Images</TableCell>
                <TableCell>Active (in use)</TableCell>
                <TableCell>Unused (tagged)</TableCell>
                <TableCell>Dangling (untagged)</TableCell>
                <TableCell>Build Cache</TableCell>
                <TableCell>Volumes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {imageStats.map(row => (
                <TableRow key={row.pod}>
                  <TableCell>{row.pod}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <StorageIcon fontSize="small" />
                      {row.total.count} ({row.total.size})
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`${row.active.count}`} style={{ backgroundColor: '#4caf50', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`${row.unused.count}`} style={{ backgroundColor: row.unused.count > 0 ? '#ff9800' : '#4caf50', color: '#fff' }} />
                    {row.unused.count > 0 && (
                      <Typography variant="caption" style={{ marginLeft: 4 }}>
                        {row.unused.size}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`${row.dangling.count}`} style={{ backgroundColor: row.dangling.count > 0 ? '#f44336' : '#4caf50', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    {row.buildCache.count} ({row.buildCache.size})
                  </TableCell>
                  <TableCell>
                    {row.volumes.count} ({row.volumes.size})
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </InfoCard>
      </Box>
    )}
      </Content>
    </Page>
  );
};
